/**
 * op-color.js — colour arithmetic for «object paint»: give a picked object a
 * new colour and keep its folds, shadows and highlights.
 *
 * No DOM. UMD: the same file runs in the page, in a worker and in node
 * (tests/js/object-paint-color.test.mjs). Preview and export run these same
 * per-pixel functions (no shader copy).
 *
 * Colour space: OKLab (Björn Ottosson, 2020; the gamut functions are his,
 * MIT). CIELAB pushes blues toward purple when the hue is held and the
 * lightness changes — exactly the move a recolour makes on every shaded pixel.
 *
 * Public API — window.OpColor / module.exports:
 *   srgbToLinear(c01) / linearToSrgb(c01)
 *   rgbToOklab(r,g,b 0..255) → [L,a,b]      oklabToRgb(L,a,b) → [r,g,b] 0..255 (clamped)
 *   oklabToLinear(L,a,b) → [r,g,b] linear (unclamped)
 *   maxChroma(L, hueA, hueB) → the largest C inside sRGB at this L and hue
 *   gamutClip(L,a,b) → [L,a,b] with the chroma pulled in at constant L and hue
 *   hexToRgb('#rrggbb') / rgbToHex([r,g,b])
 */
(function (root, factory) {
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.OpColor = api;
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    function srgbToLinear(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
    function linearToSrgb(c) {
        if (c <= 0) return 0;
        if (c >= 1) return 1;
        return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    }
    // 0..255 → linear, by table: a photo is millions of lookups.
    var LIN = new Float32Array(256);
    for (var i = 0; i < 256; i++) LIN[i] = srgbToLinear(i / 255);

    function linearToOklab(r, g, b) {
        var l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
        var m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
        var s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
        var l_ = Math.cbrt(l), m_ = Math.cbrt(m), s_ = Math.cbrt(s);
        return [
            0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
            1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
            0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
        ];
    }
    function oklabToLinear(L, a, b) {
        var l_ = L + 0.3963377774 * a + 0.2158037573 * b;
        var m_ = L - 0.1055613458 * a - 0.0638541728 * b;
        var s_ = L - 0.0894841775 * a - 1.2914855480 * b;
        var l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
        return [
            4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
            -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
            -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
        ];
    }
    // The per-pixel form: writes into o, allocates nothing (a photo is millions of calls).
    function oklabInto(r, g, b, o) {
        var l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
        var m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
        var s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
        o[0] = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
        o[1] = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
        o[2] = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
        return o;
    }
    function rgbToOklab(r, g, b) { return linearToOklab(LIN[r | 0], LIN[g | 0], LIN[b | 0]); }
    function oklabToRgb(L, a, b) {
        var lin = oklabToLinear(L, a, b);
        return [Math.round(linearToSrgb(lin[0]) * 255), Math.round(linearToSrgb(lin[1]) * 255), Math.round(linearToSrgb(lin[2]) * 255)];
    }

    /* ── gamut (Ottosson, «sRGB gamut clipping», 2021, MIT) ───────────────── */

    // Largest saturation S = C/L inside sRGB for the hue (a, b), |(a,b)| = 1.
    function maxSaturation(a, b) {
        var k0, k1, k2, k3, k4, wl, wm, ws;
        if (-1.88170328 * a - 0.80936493 * b > 1) {            // red goes negative first
            k0 = 1.19086277; k1 = 1.76576728; k2 = 0.59662641; k3 = 0.75515197; k4 = 0.56771245;
            wl = 4.0767416621; wm = -3.3077115913; ws = 0.2309699292;
        } else if (1.81444104 * a - 1.19445276 * b > 1) {      // green
            k0 = 0.73956515; k1 = -0.45954404; k2 = 0.08285427; k3 = 0.12541070; k4 = 0.14503204;
            wl = -1.2684380046; wm = 2.6097574011; ws = -0.3413193965;
        } else {                                                // blue
            k0 = 1.35733652; k1 = -0.00915799; k2 = -1.15130210; k3 = -0.50559606; k4 = 0.00692167;
            wl = -0.0041960863; wm = -0.7034186147; ws = 1.7076147010;
        }
        var S = k0 + k1 * a + k2 * b + k3 * a * a + k4 * a * b;
        var kl = 0.3963377774 * a + 0.2158037573 * b;
        var km = -0.1055613458 * a - 0.0638541728 * b;
        var ks = -0.0894841775 * a - 1.2914855480 * b;
        var l_ = 1 + S * kl, m_ = 1 + S * km, s_ = 1 + S * ks;
        var l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
        var ldS = 3 * kl * l_ * l_, mdS = 3 * km * m_ * m_, sdS = 3 * ks * s_ * s_;
        var ldS2 = 6 * kl * kl * l_, mdS2 = 6 * km * km * m_, sdS2 = 6 * ks * ks * s_;
        var f = wl * l + wm * m + ws * s;
        var f1 = wl * ldS + wm * mdS + ws * sdS;
        var f2 = wl * ldS2 + wm * mdS2 + ws * sdS2;
        return S - f * f1 / (f1 * f1 - 0.5 * f * f2);
    }
    // The hue's cusp: the lightness at which the most chroma fits, and that chroma.
    function findCusp(a, b) {
        var S = maxSaturation(a, b);
        var rgb = oklabToLinear(1, S * a, S * b);
        var Lc = Math.cbrt(1 / Math.max(rgb[0], rgb[1], rgb[2]));
        return [Lc, Lc * S];
    }
    // Where the segment from (L0, 0) to (L1, C1) leaves the gamut, as t in 0..1.
    function gamutIntersection(a, b, L1, C1, L0) {
        var cusp = findCusp(a, b), Lc = cusp[0], Cc = cusp[1], t;
        if ((L1 - L0) * Cc - (Lc - L0) * C1 <= 0) {
            t = Cc * L0 / (C1 * Lc + Cc * (L0 - L1));
        } else {
            t = Cc * (L0 - 1) / (C1 * (Lc - 1) + Cc * (L0 - L1));
            var dL = L1 - L0, dC = C1;
            var kl = 0.3963377774 * a + 0.2158037573 * b;
            var km = -0.1055613458 * a - 0.0638541728 * b;
            var ks = -0.0894841775 * a - 1.2914855480 * b;
            var ldt = dL + dC * kl, mdt = dL + dC * km, sdt = dL + dC * ks;
            var L = L0 * (1 - t) + t * L1, C = t * C1;
            var l_ = L + C * kl, m_ = L + C * km, s_ = L + C * ks;
            var l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
            var lt = 3 * ldt * l_ * l_, mt = 3 * mdt * m_ * m_, st = 3 * sdt * s_ * s_;
            var lt2 = 6 * ldt * ldt * l_, mt2 = 6 * mdt * mdt * m_, st2 = 6 * sdt * sdt * s_;
            var r0 = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s - 1;
            var r1 = 4.0767416621 * lt - 3.3077115913 * mt + 0.2309699292 * st;
            var r2 = 4.0767416621 * lt2 - 3.3077115913 * mt2 + 0.2309699292 * st2;
            var ur = r1 / (r1 * r1 - 0.5 * r0 * r2), tr = -r0 * ur;
            var g0 = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s - 1;
            var g1 = -1.2684380046 * lt + 2.6097574011 * mt - 0.3413193965 * st;
            var g2 = -1.2684380046 * lt2 + 2.6097574011 * mt2 - 0.3413193965 * st2;
            var ug = g1 / (g1 * g1 - 0.5 * g0 * g2), tg = -g0 * ug;
            var b0 = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s - 1;
            var b1 = -0.0041960863 * lt - 0.7034186147 * mt + 1.7076147010 * st;
            var b2 = -0.0041960863 * lt2 - 0.7034186147 * mt2 + 1.7076147010 * st2;
            var ub = b1 / (b1 * b1 - 0.5 * b0 * b2), tb = -b0 * ub;
            tr = ur >= 0 ? tr : Infinity; tg = ug >= 0 ? tg : Infinity; tb = ub >= 0 ? tb : Infinity;
            t += Math.min(tr, tg, tb);
        }
        return t;
    }
    // Largest chroma that fits at lightness L for the hue direction (ha, hb).
    function maxChroma(L, ha, hb) {
        if (!(L > 0) || L >= 1) return 0;
        var n = Math.hypot(ha, hb);
        if (!(n > 1e-9)) return 0;
        return Math.max(0, gamutIntersection(ha / n, hb / n, L, 1, L));
    }
    // Keep L and hue; pull chroma in to the boundary when it is outside.
    function gamutClip(L, a, b) {
        var C = Math.hypot(a, b);
        if (!(C > 1e-9)) return [Math.max(0, Math.min(1, L)), 0, 0];
        var Lc = Math.max(0, Math.min(1, L));
        var cmax = maxChroma(Lc, a, b);
        if (C <= cmax) return [Lc, a, b];
        var k = cmax / C;
        return [Lc, a * k, b * k];
    }

    /* ── fast linear → sRGB byte ─────────────────────────────────────────── */
    // 4096 steps of linear light; the dark end is where the steps matter, so
    // the table is indexed by sqrt(linear) — 1/4096 in sqrt space is finer than
    // one 8-bit level everywhere.
    var ENC_N = 4096, ENC = new Uint8Array(ENC_N + 1);
    for (var e = 0; e <= ENC_N; e++) { var q = e / ENC_N; ENC[e] = Math.round(linearToSrgb(q * q) * 255); }
    function encodeByte(v) {
        if (!(v > 0)) return 0;
        if (v >= 1) return 255;
        return ENC[(Math.sqrt(v) * ENC_N + 0.5) | 0];
    }

    /* ── CIELAB (D65) — the space the reference table is measured in ─────── */
    var XN = 0.95047, ZN = 1.08883;
    function fLab(t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; }
    function fLabInv(t) { var t3 = t * t * t; return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787; }
    function linearToLab(r, g, b) {
        var x = (0.4124 * r + 0.3576 * g + 0.1805 * b) / XN;
        var y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        var z = (0.0193 * r + 0.1192 * g + 0.9505 * b) / ZN;
        var fx = fLab(x), fy = fLab(y), fz = fLab(z);
        return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
    }
    function labToLinear(L, a, b) {
        var fy = (L + 16) / 116, fx = a / 500 + fy, fz = fy - b / 200;
        var x = XN * fLabInv(fx), y = fLabInv(fy), z = ZN * fLabInv(fz);
        return [
            3.2406 * x - 1.5372 * y - 0.4986 * z,
            -0.9689 * x + 1.8758 * y + 0.0415 * z,
            0.0557 * x - 0.2040 * y + 1.0570 * z,
        ];
    }
    function lumY(r, g, b) { return 0.2126 * r + 0.7152 * g + 0.0722 * b; }
    // L* of a linear luminance.
    function yToLstar(y) { return y > 0.008856 ? 116 * Math.cbrt(y) - 16 : 903.3 * y; }

    /* Bring an out-of-range linear colour inside 0..1 WITHOUT changing its
       luminance: pull it toward the grey of the same Y (the W3C ClipColor rule,
       in linear light). The tint and table modes keep the film's lightness by
       design, so the clip must not move it either. Writes into `o`. */
    function clipKeepY(r, g, b, o) {
        var y = lumY(r, g, b);
        if (y <= 0) { o[0] = o[1] = o[2] = 0; return o; }
        if (y >= 1) { o[0] = o[1] = o[2] = 1; return o; }
        var mn = Math.min(r, g, b), mx = Math.max(r, g, b), k = 1;
        if (mn < 0) k = Math.min(k, y / (y - mn));
        if (mx > 1) k = Math.min(k, (1 - y) / (mx - y));
        o[0] = y + (r - y) * k; o[1] = y + (g - y) * k; o[2] = y + (b - y) * k;
        return o;
    }

    /* ── «relight»: the recolour that won the lab (21.10.2026) ─────────────
       Measured on 19 real recolours (cars, dresses, sofas, shoes, hair):
       keeping the source lightness (HSL «Color», CIELAB-keep-L, OKLCH) misses
       the swatch by ΔE00 29–32 because a white car cannot become black at its
       own lightness; relight lands at 2.6 with the texture kept. The object's
       surface colour (albedo) is replaced and its shading is kept, in linear
       light:  out = A · min(S, T)^γ + max(Y − T·lit, 0)
         S = Y / lit — the pixel's shading against the object's lit level;
         T = 1.3 — above that it is a highlight: its EXCESS stays neutral,
                   the gloss of paint does not take the paint's colour;
         γ = 1 / (1 + c·ln ρ) when brightening (ρ = Y_target / lit > 1),
             c = 0.5 — a dark object's shading is mostly reflections and grain;
             brightening it 1:1 multiplied them (black sofa → cream: ΔE 6.8 at
             c = 0, 2.5 at c = 0.5; won on all four strong-brightening cases).

       Gloss of a COLOURED object (29.10.2026, Farid: «блики цвета не очень,
       неестественно» on recoloured cars). A pixel of glossy paint is the body
       colour plus the reflected light: I = s·body + h·white. The reflection
       adds no chroma, so a pixel paler than the lit body is part reflection:
       q = |I − Y|/(Y·kb), kb — the same measure on the object's lit band
       (op-object, `gloss`); its share f = (1 − q − GLOSS_M)/(1 − GLOSS_M)
       stays the light's colour and only the rest takes the new colour. The
       lit band itself (q ≈ 1) gets the swatch exactly as before. `conf` fades
       this out on a body without colour (white, silver, black): there the
       chroma is the sky's reflection or noise, and the split made a silver
       taxi blotchy and kept black streaks on a red one (12 library photos).
       Lab: 40 synthetic car scenes with true layers, true mask — highlights
       ΔE00 19.2 → 16.7, paint 16.4 → 15.8; from a coloured car 23.4 → 13.0. */
    var RELIGHT_T = 1.3, RELIGHT_C = 0.5, GLOSS_M = 0.1;
    function relightParams(rgb, lit, gloss) {
        var A = [LIN[rgb[0] | 0], LIN[rgb[1] | 0], LIN[rgb[2] | 0]];
        var yt = lumY(A[0], A[1], A[2]);
        var L = Math.max(lit, 1e-6), rho = yt / L;
        var on = !!(gloss && gloss.conf > 0 && gloss.kb > 0);
        return { A: A, yt: yt, lit: L, rho: rho, T: RELIGHT_T, gamma: rho > 1 ? 1 / (1 + RELIGHT_C * Math.log(rho)) : 1,
                 kb: on ? gloss.kb : 0, conf: on ? gloss.conf : 0 };
    }
    // Linear in, linear out (written into o, clamped to 0..1).
    function relightPixel(r, g, b, P, o) {
        var y = lumY(r, g, b), h = 0;
        if (P.conf > 0 && y > 1e-6) {
            var cr = r - y, cg = g - y, cb = b - y;
            var f = (1 - Math.sqrt(cr * cr + cg * cg + cb * cb) / (y * P.kb) - GLOSS_M) / (1 - GLOSS_M);
            if (f > 0) { h = y * (f < 1 ? f : 1) * P.conf; y -= h; }
        }
        var s = y / P.lit, sd = s < P.T ? s : P.T;
        var sc = P.gamma === 1 ? sd : Math.pow(sd, P.gamma);
        var ex = y - P.T * P.lit; if (ex < 0) ex = 0;
        ex += h;
        var x;
        x = P.A[0] * sc + ex; o[0] = x < 0 ? 0 : (x > 1 ? 1 : x);
        x = P.A[1] * sc + ex; o[1] = x < 0 ? 0 : (x > 1 ? 1 : x);
        x = P.A[2] * sc + ex; o[2] = x < 0 ? 0 : (x > 1 ? 1 : x);
        return o;
    }

    /* ── «tint»: keep the pixel's lightness, take the colour's a*b* ─────────
       The optional mode for a same-lightness swap (red → emerald dress), where
       the lab rated it realistic, and the base of the black-and-white colouring
       with a PICKED colour (film lightness kept, as E13 measured). */
    function tintParams(rgb) {
        var lab = linearToLab(LIN[rgb[0] | 0], LIN[rgb[1] | 0], LIN[rgb[2] | 0]);
        return { a: lab[1], b: lab[2] };
    }
    // Lab → linear without allocating, into o.
    function labInto(L, a, b, o) {
        var fy = (L + 16) / 116, fx = a / 500 + fy, fz = fy - b / 200;
        var x = XN * fLabInv(fx), y = fLabInv(fy), z = ZN * fLabInv(fz);
        o[0] = 3.2406 * x - 1.5372 * y - 0.4986 * z;
        o[1] = -0.9689 * x + 1.8758 * y + 0.0415 * z;
        o[2] = 0.0557 * x - 0.2040 * y + 1.0570 * z;
        return o;
    }
    function tintPixel(r, g, b, P, o) {
        var L = yToLstar(lumY(r, g, b));
        labInto(L, P.a, P.b, o);
        return clipKeepY(o[0], o[1], o[2], o);
    }

    /* ── reference table: L* → (a*, b*) of the reference region ────────────
       Black-and-white by reference, the lab's winner (E9, 21.10.2026): the
       same-photo reference ΔE00 2.8 against DDColor's 14.5; a reference from
       ANOTHER photo of the same kind of object 11.4 against 14.5 — beat DDColor
       on 21 of 31 objects. 64 bins over L* 0..100, Gaussian σ = 1.5 bins, flat
       outside the reference's own L range. The film's lightness is KEPT by
       default: matching the reference's brightness won only when the reference
       was the same scene, and turned a dark greatcoat beige-white with the
       user's colourised photo (E13). */
    var LUT_N = 64;
    function buildLut(Ls, As, Bs, n) {
        var cnt = new Float64Array(LUT_N), sa = new Float64Array(LUT_N), sb = new Float64Array(LUT_N);
        for (var i = 0; i < n; i++) {
            var k = Math.max(0, Math.min(LUT_N - 1, (Ls[i] / 100 * LUT_N) | 0));
            cnt[k]++; sa[k] += As[i]; sb[k] += Bs[i];
        }
        var sig = 1.5, ker = [];
        for (var d = -6; d <= 6; d++) ker.push(Math.exp(-0.5 * (d / sig) * (d / sig)));
        var cs = new Float64Array(LUT_N), as_ = new Float64Array(LUT_N), bs_ = new Float64Array(LUT_N);
        for (var j = 0; j < LUT_N; j++) {
            for (var q = -6; q <= 6; q++) {
                var jj = j + q;
                if (jj < 0 || jj >= LUT_N) continue;
                var wk = ker[q + 6];
                cs[j] += cnt[jj] * wk; as_[j] += sa[jj] * wk; bs_[j] += sb[jj] * wk;
            }
        }
        var A = new Float32Array(LUT_N), B = new Float32Array(LUT_N), valid = [];
        for (var v = 0; v < LUT_N; v++) if (cs[v] > 0.5) { A[v] = as_[v] / cs[v]; B[v] = bs_[v] / cs[v]; valid.push(v); }
        if (!valid.length) return null;
        for (var u = 0; u < LUT_N; u++) {
            if (cs[u] > 0.5) continue;
            var best = valid[0];
            for (var w = 1; w < valid.length; w++) if (Math.abs(valid[w] - u) < Math.abs(best - u)) best = valid[w];
            A[u] = A[best]; B[u] = B[best];
        }
        return { n: LUT_N, a: A, b: B };
    }
    function lutLookup(lut, L) {
        var x = L / 100 * lut.n - 0.5;
        if (x <= 0) return [lut.a[0], lut.b[0]];
        if (x >= lut.n - 1) return [lut.a[lut.n - 1], lut.b[lut.n - 1]];
        var i = x | 0, f = x - i;
        return [lut.a[i] + (lut.a[i + 1] - lut.a[i]) * f, lut.b[i] + (lut.b[i + 1] - lut.b[i]) * f];
    }
    // `bright` 0..1 — the optional pull of the film's brightness toward the
    // reference's (median luminance ratio, contrast kept); 0 = the film's own.
    function lutPixel(r, g, b, P, o) {
        var y = lumY(r, g, b);
        if (P.yScale !== 1) { y *= P.yScale; if (y > 1) y = 1; }
        var L = yToLstar(y), lut = P.lut, aa, bb;
        var x = L / 100 * lut.n - 0.5;
        if (x <= 0) { aa = lut.a[0]; bb = lut.b[0]; }
        else if (x >= lut.n - 1) { aa = lut.a[lut.n - 1]; bb = lut.b[lut.n - 1]; }
        else { var i = x | 0, f = x - i; aa = lut.a[i] + (lut.a[i + 1] - lut.a[i]) * f; bb = lut.b[i] + (lut.b[i + 1] - lut.b[i]) * f; }
        labInto(L, aa, bb, o);
        return clipKeepY(o[0], o[1], o[2], o);
    }

    function hexToRgb(hex) {
        var h = String(hex || '').replace(/^#/, '').trim();
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
        var n = parseInt(h, 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    function rgbToHex(rgb) {
        return '#' + rgb.map(function (v) { var s = Math.max(0, Math.min(255, Math.round(v))).toString(16); return s.length === 1 ? '0' + s : s; }).join('');
    }

    return {
        LIN: LIN, encodeByte: encodeByte,
        linearToLab: linearToLab, labToLinear: labToLinear, lumY: lumY, yToLstar: yToLstar, clipKeepY: clipKeepY,
        RELIGHT_T: RELIGHT_T, RELIGHT_C: RELIGHT_C, GLOSS_M: GLOSS_M,
        relightParams: relightParams, relightPixel: relightPixel,
        tintParams: tintParams, tintPixel: tintPixel,
        buildLut: buildLut, lutLookup: lutLookup, lutPixel: lutPixel,
        srgbToLinear: srgbToLinear, linearToSrgb: linearToSrgb,
        linearToOklab: linearToOklab, oklabToLinear: oklabToLinear, oklabInto: oklabInto,
        rgbToOklab: rgbToOklab, oklabToRgb: oklabToRgb,
        maxSaturation: maxSaturation, findCusp: findCusp, gamutIntersection: gamutIntersection,
        maxChroma: maxChroma, gamutClip: gamutClip,
        hexToRgb: hexToRgb, rgbToHex: rgbToHex,
    };
}));
